import type { NotificationDTO } from '@devrobot/shared';
import { Bell, Check, Settings2 } from 'lucide-react';
import { Link } from 'react-router';
import { useMarkAllRead, useMarkRead, useNotifications, useUnreadCount } from '../../api/notifications';
import { MenuPopover } from '../MenuPopover';
import { RelativeTime } from '../RelativeTime';
import { SEVERITY } from './severity';

/** Beyond this the exact number stops being useful and starts being noise. */
const MAX_COUNT = 99;

export function NotificationBell() {
  const unread = useUnreadCount().data?.unread ?? 0;
  const label = unread === 0 ? 'Notifications' : `Notifications, ${unread} unread`;

  return (
    <MenuPopover
      label={label}
      title={label}
      triggerClassName="btn btn-square btn-ghost btn-sm"
      panelClassName="w-[22rem] max-w-[calc(100vw-1rem)] p-0"
      trigger={
        <span className="indicator">
          {unread > 0 && (
            <span className="indicator-item badge badge-error badge-xs translate-x-1 -translate-y-1 px-1 font-semibold tabular-nums">
              {unread > MAX_COUNT ? `${MAX_COUNT}+` : unread}
            </span>
          )}
          <Bell className="size-[1.125rem]" aria-hidden="true" />
        </span>
      }
    >
      {(close) => <BellPanel close={close} unread={unread} />}
    </MenuPopover>
  );
}

function BellPanel({ close, unread }: { close: () => void; unread: number }) {
  const query = useNotifications();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const items = (query.data?.pages.flatMap((p) => p.items) ?? []).slice(0, 10);

  return (
    <div className="flex max-h-[min(30rem,80dvh)] flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-hairline px-3 py-2">
        <h2 className="text-sm font-semibold">Notifications</h2>
        <div className="flex items-center gap-1">
          {unread > 0 && (
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => markAllRead.mutate({})} disabled={markAllRead.isPending}>
              <Check className="size-3.5" aria-hidden="true" />
              Mark all read
            </button>
          )}
          <Link to="/settings?tab=notifications" className="btn btn-square btn-ghost btn-xs" onClick={close} aria-label="Notification settings">
            <Settings2 className="size-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {query.isPending ? (
          <p className="px-3 py-6 text-center text-sm text-base-content/60">Loading…</p>
        ) : items.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-base-content/60">Nothing yet. Deploys, failures and alerts show up here.</p>
        ) : (
          <ul className="divide-y divide-hairline">
            {items.map((n) => (
              <BellRow key={n.id} n={n} onOpen={() => { if (!n.readAt) markRead.mutate([n.id]); close(); }} />
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-hairline p-2">
        <Link to="/notifications" className="btn btn-ghost btn-sm btn-block" onClick={close}>
          See all notifications
        </Link>
      </div>
    </div>
  );
}

function BellRow({ n, onOpen }: { n: NotificationDTO; onOpen: () => void }) {
  const s = SEVERITY[n.severity];
  const Icon = s.icon;
  const className = `flex gap-2.5 px-3 py-2.5 text-left ${n.href ? 'hover:bg-base-200/60' : ''} ${n.readAt ? 'opacity-60' : ''}`;
  const body = (
    <>
      <span className={`${s.tint} mt-0.5 grid size-7 shrink-0 place-items-center rounded-full`}>
        <Icon className="size-3.5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{n.title}</span>
          {!n.readAt && <span className="size-1.5 shrink-0 rounded-full bg-primary" aria-label="Unread" />}
        </span>
        {n.body && <span className="mt-0.5 line-clamp-2 block text-xs text-base-content/70">{n.body}</span>}
        <span className="mt-1 flex items-center gap-1.5 text-[0.6875rem] text-base-content/55">
          <span className="sr-only">{s.label}. </span>
          <RelativeTime value={n.createdAt} />
          {n.projectKey && <span className="truncate">· {n.projectKey}{n.environment ? `/${n.environment}` : ''}</span>}
        </span>
      </span>
    </>
  );
  // Without an href there is nothing to open, so the row is not a link.
  return (
    <li>
      {n.href ? (
        <Link to={n.href} onClick={onOpen} className={className}>
          {body}
        </Link>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
}
